import { createApi } from "@reduxjs/toolkit/query/react";
import { baseAppQuery } from "shared/api/baseQuery";
import { Filter } from "shared/types/filters";
import {
  NoticeData,
  AttachmentInfo,
  AttachmentField,
  AttachmentFieldValue,
} from "features/notice/types/notice";
import { UploadedFile } from "shared/types/dropzone";
import { StatusHistory } from "shared/types/statuses";
import { snackbarOnQueryStarted } from "middleware/snackbarOnQueryStarted";

const noticesApi: any = createApi({
  reducerPath: "noticesApi",
  baseQuery: baseAppQuery,
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  tagTypes: ["notices", "noticeAttachments"],
  endpoints: (builder) => ({
    getNoticeList: builder.query<
      NoticeData,
      {
        applicationId?: string;
        filter: Filter;
      
        orgId: string;
      }
    >({
      query: ({ applicationId, filter,  orgId }) => {
        const queryParams = new URLSearchParams();
        if (applicationId) queryParams.set("applicationId", applicationId);
        if (filter) {
          queryParams.set("page", filter.pageNumber.toString());
          queryParams.set("size", filter.pageSize.toString());
          if (orgId) queryParams.set("orgId", orgId);
        }
        return `notice/fetch-all?${queryParams}`;
      },
      providesTags: ["notices", "noticeAttachments"],
    }),
    getNoticeById: builder.query<
      NoticeData,
      { id: string;  }
    >({
      query: ({ id }) => `notice/${id}`,
    }),
    getNoticeListCurrent: builder.query<
      NoticeData,
      { applicationId: string; filter: Filter; }
    >({
      query: ({ applicationId, filter }) => {
        const queryParams = new URLSearchParams();
        if (filter) {
          queryParams.set("page", filter.pageNumber.toString());
          queryParams.set("size", filter.pageSize.toString());
        }
        return `notice/fetch-all-current?applicationId=${applicationId}&${queryParams}`;
      },
      providesTags: ["notices", "noticeAttachments"],
    }),
    getNoticeByIdCurrent: builder.query<
      NoticeData,
      { id: string;  }
    >({
      query: ({ id }) => `notice/current/${id}`,
    }),
    getNoticeHistoryById: builder.query<
      StatusHistory[],
      { id: string;  }
    >({
      query: ({ id }) => `notice/status-history/${id}`,
    }),
    getNoticeAttachmentById: builder.query<
      AttachmentInfo,
      { id: string;  }
    >({
      query: ({ id }) => `notice/fetch-attachment/${id}`,
      providesTags: ["noticeAttachments"],
    }),
    getNoticeDocumentById: builder.query<
      UploadedFile,
      { id: string;  }
    >({
      query: ({ id }) => `notice/document/${id}`,
    }),
    createNotice: builder.mutation<
      NoticeData,
      { applicationId: string;  }
    >({
      query: ({ applicationId }) => ({
        url: `notice?applicationId=${applicationId}`,
        method: "POST",
      }),
      invalidatesTags: ["notices"],
    }),
    revisionNotice: builder.mutation<
      NoticeData,
      { id: string; comment: string;  }
    >({
      query: ({ id, comment}) => {
        return {
          url: `notice/revision`,
          method: "POST",
          body: { noticeId: id, comment },
        };
      },
      invalidatesTags: ["notices", "noticeAttachments"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"revisionSuccess",
        errorMessage:"revisionError",
      })
    }),
    addNoticeAttachment: builder.mutation<
      AttachmentInfo,
      { id: string;  }
    >({
      query: ({ id }) => ({
        url: `notice/attachment-add?noticeId=${id}`,
        method: "POST",
      }),
      invalidatesTags: ["noticeAttachments"],
      onQueryStarted: snackbarOnQueryStarted({
        errorMessage:"attachmentAddError",
      })
    }),
    updateNoticeAttachment: builder.mutation<
      AttachmentInfo,
      { data: AttachmentField;  }
    >({
      query: ({ data }) => {
        let formData = new FormData();
        const attachment = { ...data } as AttachmentFieldValue;
        Object.keys(attachment).forEach((key: string) => {
          if (key !== "documents") formData.append(key, attachment[key]);
        });
        if (data?.documents?.length) {
          const files = [...data.documents] as unknown as File[];
          files.forEach((file) => {
            formData.append("files", file);
          });
        }
        return {
          url: `notice/attachment-update`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["noticeAttachments"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"saveAttachmentSuccess",
        errorMessage:"saveAttachmentError",
      })
    }),
    deleteNoticeAttachment: builder.mutation<
      AttachmentInfo,
      { noticeId: string; attachmentId: string;  }
    >({
      query: ({ noticeId, attachmentId }) => ({
        url: `notice/attachment-delete?attachmentId=${attachmentId}&noticeId=${noticeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["noticeAttachments"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"deleteAttachmentSuccess",
        errorMessage:"deleteAttachmentError",
      })
    }),
  }),
});

export const {
  useGetNoticeListQuery,
  useGetNoticeByIdQuery,
  useGetNoticeListCurrentQuery,
  useGetNoticeByIdCurrentQuery,
  useGetNoticeHistoryByIdQuery,
  useGetNoticeAttachmentByIdQuery,
  useGetNoticeDocumentByIdQuery,
  useLazyGetNoticeDocumentByIdQuery,
  useCreateNoticeMutation,
  useRevisionNoticeMutation,
  useAddNoticeAttachmentMutation,
  useUpdateNoticeAttachmentMutation,
  useDeleteNoticeAttachmentMutation,
} = noticesApi;
export default noticesApi;
